import React, { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import dayjs from "dayjs";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const STATUS_STYLE = {
  APPROVED: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  REJECTED: "text-red-400 bg-red-500/10 border-red-500/20",
};

export default function OvertimePanel({ loading, items = [], onApprove, onReject, month }) {
  const [comments, setComments] = useState({});
  const setComment = (id, val) => setComments(c => ({ ...c, [id]: val }));

  const selectedMonth = month || new Date().toISOString().slice(0, 7);

  const { totalHours, chartData } = useMemo(() => {
    const filtered = items.filter(x => x.date?.startsWith(selectedMonth));
    const total    = filtered.reduce((s, x) => s + Number(x.hours || 0), 0);

    // Group by dept for chart
    const byDept = {};
    filtered.forEach(x => {
      byDept[x.dept || "Other"] = (byDept[x.dept || "Other"] || 0) + Number(x.hours || 0);
    });
    const chart = Object.entries(byDept).map(([dept, hours]) => ({ dept, hours: parseFloat(hours.toFixed(1)) }));

    return { totalHours: total.toFixed(1), chartData: chart };
  }, [items, selectedMonth]);

  const pending = items.filter(x => !x.status);
  const decided = items.filter(x => x.status);

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Overtime Approvals</div>
      <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 rounded-lg bg-white/5 animate-pulse" />)}</div>
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Overtime Approvals</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-white/30">{selectedMonth}</span>
          <span className="text-sm font-bold text-[#60a5fa]">{totalHours}h total</span>
        </div>
      </div>

      <div className="p-5 space-y-4">

        {/* By dept bar chart */}
        {chartData.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Hours by Department</p>
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="dept" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v}h`, "Overtime"]} />
                <Bar dataKey="hours" fill="#60a5fa" fillOpacity={0.7} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pending approvals */}
        {items.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-2">No overtime records.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {[...pending, ...decided].map(x => {
              const isDecided = !!x.status;
              const statusStyle = STATUS_STYLE[x.status] || "";
              return (
                <div key={x.id} className="rounded-xl border border-[#00ff90]/15 bg-[#0d0d0d] p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-mono text-white/30">{x.id}</span>
                        <span className="text-sm text-white/70">{x.hours}h</span>
                        <span className="text-xs text-white/30">{x.dept}</span>
                      </div>
                      <p className="text-xs text-white/25">
                        {x.employeeId} · {x.date}
                        {x.submittedAt && ` · ${x.submittedAt.replace("T", " ").slice(0, 16)}`}
                      </p>
                    </div>

                    {isDecided ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${statusStyle}`}>{x.status}</span>
                    ) : (
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <input
                          placeholder="Comment…"
                          value={comments[x.id] || ""}
                          onChange={e => setComment(x.id, e.target.value)}
                          className="bg-black border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none w-28 focus:border-[#00ff90]/30 transition-colors"
                        />
                        <button onClick={() => onApprove?.(x.id, comments[x.id] || "")}
                          className="text-xs px-2.5 py-1 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                          ✓
                        </button>
                        <button onClick={() => onReject?.(x.id, comments[x.id] || "")}
                          className="text-xs px-2.5 py-1 rounded-lg border border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400 transition-colors">
                          ✗
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}